import { useQuery } from '@tanstack/react-query';
import { contentApi, panoramaApi, webstandaloneApi } from '@webdev/upload-management';
import { fromPairs, pick } from 'lodash-es';
import uuid from 'uuid';
import { rawFetchWithToken } from './fetch';
import {
  Content,
  ContentDetails,
  Folder,
  FolderHierarchy,
  GalleryContent,
  isPanoramaContent,
  isWebStandaloneContent,
  PanoramaContent,
  PanoramaDetails,
  ROOT_FOLDER_ID,
  ROOT_FOLDER_NAME,
  RootFolder,
  WebStandaloneContent,
  WebStandaloneDetails,
} from './UploadManagementServiceTypes';

const makeAbsoluteUrl = (url: string) => new URL(url, window.location.origin).toString();

const getFolderContentApi = (noAuth = false) =>
  new contentApi.FolderApi(
    new contentApi.Configuration({
      basePath: makeAbsoluteUrl('/api/um/content'),
      apiKey: noAuth ? undefined : `Bearer ${JSON.parse(localStorage['kl.jwtToken'])}`,
    }),
  );
const getWebStandaloneContentApi = () =>
  new contentApi.WebStandaloneApi(
    new contentApi.Configuration({
      basePath: makeAbsoluteUrl('/api/um/content'),
      apiKey: `Bearer ${JSON.parse(localStorage['kl.jwtToken'])}`,
    }),
  );
const getPanoramaContentApi = () =>
  new contentApi.PanoramaApi(
    new contentApi.Configuration({
      basePath: makeAbsoluteUrl('/api/um/content'),
      apiKey: `Bearer ${JSON.parse(localStorage['kl.jwtToken'])}`,
    }),
  );
const getGalleryContentApi = () =>
  new contentApi.PanoramaTourApi(
    new contentApi.Configuration({
      basePath: makeAbsoluteUrl('/api/um/content'),
      apiKey: `Bearer ${JSON.parse(localStorage['kl.jwtToken'])}`,
    }),
  );
const getGalleryContentSharedApi = () =>
  new contentApi.PanoramaTourApi(
    new contentApi.Configuration({
      basePath: makeAbsoluteUrl('/api/um/content'),
      apiKey: undefined,
    }),
  );
const getAccountPanoramaApi = () =>
  new panoramaApi.AccountPanoramaApi(
    new panoramaApi.Configuration({
      basePath: makeAbsoluteUrl('/api/um/account-panorama'),
      apiKey: undefined,
    }),
  );
const getAccountWebStandaloneApi = () =>
  new webstandaloneApi.AccountWebStandaloneApi(
    new webstandaloneApi.Configuration({
      basePath: makeAbsoluteUrl('/api/um/account-webstandalone'),
      apiKey: undefined,
    }),
  );

export interface CreateFolderArgs {
  parentGuid: string | null;
  name: string;
}

const sanitizeContentResponse = (rawContent: any): Content => ({
  ...pick(rawContent, [
    'guid',
    'parentGuid',
    'name',
    'createdAt',
    'modifiedAt',
    'sharedAccessKey',
    'parentAccessKey',
    'chaosCollaborationUrl',
  ]),
  contentType: rawContent.type,
});

export const sanitizeFolderResponse = (rawFolder: any): Folder => ({
  ...sanitizeContentResponse(rawFolder),
  contents: (rawFolder.children || []).map(sanitizeContentResponse),
});

const sanitizeHierarchyResponse = (rawHierarchy: any): FolderHierarchy => ({
  ...pick(rawHierarchy, ['guid', 'parentGuid', 'name', 'createdAt', 'modifiedAt']),
  subHierarchy: (rawHierarchy.children || []).map(sanitizeHierarchyResponse),
});

export const sanitizePanoramaDetails = (rawDetails: any): PanoramaDetails => ({
  detailType: 'Panorama',
  ...pick(
    rawDetails,
    'viewLink',
    'thumbnailDownloadUrl',
    'isStereo',
    'projectName',
    'title',
    'panoramaGuid',
    'imageDownloadUrl',
    'camera',
  ),
});

export const sanitizeWebStandaloneDetails = (rawDetails: any): WebStandaloneDetails => ({
  detailType: 'WebStandalone',
  ...pick(rawDetails, 'viewLink'),
});

const filterMigratedContent = (contents: any) => {
  return contents.filter((item: any) => !item.chaosCollaborationUrl);
};

const filterMigratedFolderContent = async (contents: any) => {
  const filteredContent = await Promise.all(
    contents.map(async (item: any) => {
      if (item.contentType.name === 'Folder') {
        const guid = item.guid;
        const response = await getFolderContentApi().v1FoldersGuidGet({ guid });
        const folder = sanitizeFolderResponse(response);
        if (folder === null) {
          return null;
        }

        if (folder.contents.length === 0) {
          return item;
        }

        const filteredContent = await filterMigratedContent(folder.contents);
        return filteredContent.length > 0 ? item : null;
      }

      return !item.chaosCollaborationUrl ? item : null;
    }),
  );

  return filteredContent.filter(Boolean);
};

const filterMigratedFolderRootContent = (contents: any) => {
  return contents.filter((item: any) => {
    if (item.type.name === 'Folder') {
      if (Array.isArray(item.children)) {
        if (item.children.length === 0) {
          return true;
        }

        const allHaveCollaborationUrl = item.children.every(
          (child: any) =>
            child.chaosCollaborationUrl !== null && child.chaosCollaborationUrl !== undefined,
        );
        return !allHaveCollaborationUrl;
      }
    }
    return true;
  });
};

export const getContentDetails = async (contents: Content[]): Promise<ContentDetails> => {
  const details = await Promise.all(
    contents.map(async (item) => {
      const [shareGuid, contentGuid] = item.sharedAccessKey.split('/');
      if (isPanoramaContent(item)) {
        const panoramaDetails = await getSharedAccountPanorama({ shareGuid, contentGuid });
        return [item.guid, sanitizePanoramaDetails(panoramaDetails)];
      }
      if (isWebStandaloneContent(item)) {
        const webStandaloneDetails = await getSharedAccountWebStandalone({
          shareGuid,
          contentGuid,
        });
        return [item.guid, sanitizeWebStandaloneDetails(webStandaloneDetails)];
      }
      return [item.guid, null];
    }),
  );

  return fromPairs(details);
};

export const getFolderHierarchy = (): Promise<FolderHierarchy> => {
  return getFolderContentApi()
    .v1FoldersHierarchyGet()
    .then((hierarchy) => {
      const folderHierarchy: FolderHierarchy = {
        guid: ROOT_FOLDER_ID,
        name: ROOT_FOLDER_NAME,
        createdAt: '',
        subHierarchy: hierarchy.map(sanitizeHierarchyResponse),
      };
      return folderHierarchy;
    });
};

export const useGetFolderHierarchy = (queryFn: () => Promise<FolderHierarchy>, extraKey = '') =>
  useQuery({ queryKey: ['getFolderHierarchy', extraKey], queryFn });

export const getRootFolder = () =>
  getFolderContentApi()
    .v1FoldersRootGet()
    .then((folders) => {
      const filteredFolderContents = filterMigratedFolderRootContent(folders);
      const sanitizedContents = filteredFolderContents.map(sanitizeContentResponse);
      const filteredContents = filterMigratedContent(sanitizedContents);
      const rootFolder: RootFolder = {
        guid: ROOT_FOLDER_ID,
        name: ROOT_FOLDER_NAME,
        sharedAccessKey: '',
        contentType: { name: 'root', guid: 'root' },
        contents: filteredContents,
        createdAt: '',
      };

      return rootFolder;
    });

export const getFolder = async (guid: string) => {
  try {
    const response = await getFolderContentApi().v1FoldersGuidGet({ guid });
    const sanitizedFolder = sanitizeFolderResponse(response);

    const filteredContents = await filterMigratedFolderContent(sanitizedFolder.contents);
    sanitizedFolder.contents = filteredContents;

    return sanitizedFolder;
  } catch (e: any) {
    if (e.status === 404) {
      return null;
    }

    throw new Error(e.statusText);
  }
};

const sanitizeGalleryResponse = (rawContent: any): GalleryContent => ({
  ...pick(rawContent, [
    'guid',
    'name',
    'createdAt',
    'modifiedAt',
    'sharedAccessKey',
    'parentAccessKey',
    'chaosCollaborationUrl',
  ]),
  parentGuid: rawContent.parentGuid === null ? ROOT_FOLDER_ID : rawContent.parentGuid,
  contentType: { name: 'Gallery', guid: rawContent.type.guid },
});

export const getAllGalleries = async () => {
  try {
    const response = await getGalleryContentApi().v1PanoramaToursGet();
    const sanitizedResponse = response.map(sanitizeGalleryResponse);
    const filteredResponse = filterMigratedContent(sanitizedResponse);
    return filteredResponse;
  } catch (e: any) {
    if (e.status === 404) {
      return null;
    }
    throw new Error(e.statusText);
  }
};

export const deleteFolder = async (guid: string) => {
  return getFolderContentApi()
    .v1FoldersGuidDeleteRaw({ guid })
    .then(({ raw }) => raw.ok);
};

export const getGalleryContent = async (guid: string) => {
  const response = await getGalleryContentApi().v1PanoramaToursGuidGet({ guid });
  return sanitizeContentResponse(response);
};

export const getGalleryContentShared = async (shareGuid: string, contentGuid: string) => {
  const response = await getGalleryContentSharedApi().v1PanoramaToursSharedShareGuidContentGuidGet({
    shareGuid,
    contentGuid,
  });
  return sanitizeContentResponse(response);
};

export const renameFolder = async (guid: string, name: string) => {
  const { raw } = await getFolderContentApi().v1FoldersGuidPatchRaw({
    patchFolderRequest: { name },
    guid,
  });
  return raw.ok;
};

export const moveFolder = async (guid: string, parentGuid: string) => {
  return getFolderContentApi()
    .v1FoldersGuidPatchRaw({
      guid,
      patchFolderRequest: {
        parentGuid: parentGuid === ROOT_FOLDER_ID ? (null as unknown as string) : parentGuid,
      },
    })
    .then(({ raw }) => raw.ok);
};

export const moveWebStandalone = async (guid: string, parentGuid: string) => {
  return getWebStandaloneContentApi()
    .v1WebstandalonesGuidPatchRaw({
      guid,
      patchWebStandaloneRequest: {
        parentGuid: parentGuid === ROOT_FOLDER_ID ? (null as unknown as string) : parentGuid,
      },
    })
    .then(({ raw }) => raw.ok);
};

export const movePanorama = async (guid: string, parentGuid: string) => {
  return getPanoramaContentApi()
    .v1PanoramasGuidPatchRaw({
      guid,
      patchPanoramaRequest: {
        parentGuid: parentGuid === ROOT_FOLDER_ID ? (null as unknown as string) : parentGuid,
      },
    })
    .then(({ raw }) => raw.ok);
};

export const moveGallery = async (guid: string, parentGuid: string) => {
  return getGalleryContentApi()
    .v1PanoramaToursGuidPatchRaw({
      guid,
      patchPanoramaTourRequest: {
        parentGuid: parentGuid === ROOT_FOLDER_ID ? (null as unknown as string) : parentGuid,
      },
    })
    .then(({ raw }) => raw.ok);
};

export const deleteWebStandalone = async (guid: string) => {
  return getWebStandaloneContentApi()
    .v1WebstandalonesGuidDeleteRaw({ guid })
    .then(({ raw }) => raw.status === 200);
};

export const deletePanorama = async (guid: string) => {
  return getPanoramaContentApi()
    .v1PanoramasGuidDeleteRaw({ guid })
    .then(({ raw }) => raw.ok);
};

export const deleteGallery = async (guid: string) => {
  return getGalleryContentApi()
    .v1PanoramaToursGuidDeleteRaw({ guid })
    .then(({ raw }) => raw.ok);
};

export const createFolder = async (args: CreateFolderArgs) => {
  return getFolderContentApi()
    .v1FoldersGuidPostRaw({ guid: uuid.v4(), createFolderRequest: args })
    .then(({ raw }) => raw.ok);
};

export const getSharedFolderContent = async ({
  contentGuid,
  shareGuid,
}: {
  shareGuid: string;
  contentGuid: string;
}) => {
  try {
    const response = await getFolderContentApi(true).v1FoldersSharedShareGuidContentGuidGet({
      contentGuid,
      shareGuid,
    });

    return sanitizeFolderResponse(response);
  } catch (e: any) {
    if (e.status === 404) {
      return null;
    }

    throw new Error(e.statusText);
  }
};

export const generateNewFolderSharedAccessKey = async (guid: string) => {
  const response = await getFolderContentApi().v1FoldersGuidPatch({
    guid,
    patchFolderRequest: { shareGuid: uuid.v4() },
  });
  return sanitizeFolderResponse(response);
};

export const sendFolderSharedPerMail = async (data: {
  guid: string;
  email: string;
}): Promise<void> => {
  const response = await rawFetchWithToken(`/api/um/share-email/folders/${data.guid}`, {
    method: 'POST',
    body: JSON.stringify({
      email: data.email,
    }),
  });

  if (!response.ok) {
    throw new Error(response.statusText);
  }
};

export const renamePanorama = async (guid: string, name: string) => {
  const { raw } = await getPanoramaContentApi().v1PanoramasGuidPatchRaw({
    guid,
    patchPanoramaRequest: { name },
  });
  return raw.ok;
};

export const renameWebStandalone = async (guid: string, name: string) => {
  const { raw } = await getWebStandaloneContentApi().v1WebstandalonesGuidPatchRaw({
    guid,
    patchWebStandaloneRequest: { name },
  });
  return raw.ok;
};

export const renameGallery = async (guid: string, name: string) => {
  const { raw } = await getGalleryContentApi().v1PanoramaToursGuidPatchRaw({
    guid,
    patchPanoramaTourRequest: { name },
  });
  return raw.ok;
};

export const generateNewPanoramaSharedAccessKey = async (guid: string): Promise<PanoramaContent> =>
  sanitizeContentResponse(
    await getPanoramaContentApi().v1PanoramasGuidPatch({
      guid,
      patchPanoramaRequest: { shareGuid: uuid.v4() },
    }),
  );

export const generateNewWebstandaloneSharedAccessKey = async (
  guid: string,
): Promise<WebStandaloneContent> =>
  sanitizeContentResponse(
    await getWebStandaloneContentApi().v1WebstandalonesGuidPatch({
      guid,
      patchWebStandaloneRequest: { shareGuid: uuid.v4() },
    }),
  );

export const generateNewGallerySharedAccessKey = async (guid: string): Promise<GalleryContent> =>
  sanitizeContentResponse(
    await getGalleryContentApi().v1PanoramaToursGuidPatch({
      guid,
      patchPanoramaTourRequest: { shareGuid: uuid.v4() },
    }),
  );

export const sendWebstandaloneSharedPerMail = async (data: {
  guid: string;
  email: string;
}): Promise<void> => {
  const response = await rawFetchWithToken(`/api/um/share-email/webstandalones/${data.guid}`, {
    method: 'POST',
    body: JSON.stringify({
      email: data.email,
    }),
  });

  if (!response.ok) {
    throw new Error(response.statusText);
  }
};

export const sendPanoramaSharedPerMail = async (data: {
  guid: string;
  email: string;
}): Promise<void> => {
  const response = await rawFetchWithToken(`/api/um/share-email/panoramas/${data.guid}`, {
    method: 'POST',
    body: JSON.stringify({
      email: data.email,
    }),
  });

  if (!response.ok) {
    throw new Error(response.statusText);
  }
};

export const sendGallerySharedPerMail = async (data: {
  guid: string;
  email: string;
}): Promise<void> => {
  const response = await rawFetchWithToken(`/api/um/share-email/galleries/${data.guid}`, {
    method: 'POST',
    body: JSON.stringify({
      email: data.email,
    }),
  });

  if (!response.ok) {
    throw new Error(response.statusText);
  }
};

export const getSharedAccountPanorama = (
  requestOptions: panoramaApi.AccountPanoramaSharedShareGuidContentGuidGetRequest,
) => getAccountPanoramaApi().accountPanoramaSharedShareGuidContentGuidGet(requestOptions);

export const getSharedAccountWebStandalone = (
  requestOptions: webstandaloneApi.ApiAccountWebstandaloneSharedShareGuidContentGuidGetRequest,
) =>
  getAccountWebStandaloneApi().apiAccountWebstandaloneSharedShareGuidContentGuidGet(requestOptions);

export const getSharedFolderHierarchy = async (shareGuid?: string) => {
  if (!shareGuid) {
    return;
  }
  try {
    const response = await getFolderContentApi(true).v1FoldersSharedShareGuidHierarchyGet({
      shareGuid,
    });
    return sanitizeHierarchyResponse(response[0]);
  } catch (e: any) {
    if (e.status === 404) {
      return;
    }

    throw new Error(e);
  }
};
